/** Fantasma por contornos: o resto do modelo fica só com as arestas.
 *
 *  O fantasma translúcido (estilo `ghost` do Highlighter) não deixa ver
 *  através: o Fragments desenha a cor translúcida por cima do material
 *  original, que continua opaco, e as fachadas tapavam os elementos de
 *  um conflito (Rui, 17/10/2026: "um modo de transparência que continue
 *  a dar algum destaque às linhas de contorno [...] sem ocultar o
 *  resto", como no usBIM).
 *
 *  Como se faz: a textura da visibilidade das arestas de cada modelo
 *  (edgeVisibility.ts) marca estes elementos com 128. As arestas deles
 *  desenham-se esbatidas, e o shader das faces (paintEngine.ts) descarta
 *  as faces deles. Os elementos continuam visíveis para o Fragments.
 *
 *  A primeira versão (beta.196) escondia as faces com a visibilidade do
 *  Fragments. Com um modelo inteiro em fantasma o Fragments apagava os
 *  tiles todos, e as arestas, que são filhas dos tiles, iam com eles
 *  (medido a 18/10). E obrigava a esconder isto do resto da plataforma
 *  (contagem de escondidos, Mostrar tudo, viewpoints). Pelo shader, nada
 *  disso: não se esconde nada.
 *
 *  Este módulo só guarda, por modelo, que elementos estão em fantasma. */

export type EstiloDoFantasma = 'contornos' | 'translucido'

/** Opacidade das arestas dos elementos em fantasma (as do conflito
 *  ficam a cheio). */
export const OPACIDADE_DAS_ARESTAS_EM_FANTASMA = 0.45

/** Por modelo: os elementos (localIds) em fantasma por contornos. */
const emFantasma = new Map<string, Set<number>>()

export function escondidosPeloFantasmaEm(modelId: string): ReadonlySet<number> {
  return emFantasma.get(modelId) ?? new Set()
}

/** O que mudou para passar de `antes` a `depois`. */
export function diferenca(
  antes: ReadonlySet<number>,
  depois: ReadonlySet<number>,
): { mostrar: number[]; esconder: number[] } {
  const mostrar: number[] = []
  const esconder: number[] = []
  for (const id of antes) if (!depois.has(id)) mostrar.push(id)
  for (const id of depois) if (!antes.has(id)) esconder.push(id)
  return { mostrar, esconder }
}

/** Põe em fantasma estes elementos do modelo (e tira os que deixaram de
 *  lá estar). Devolve se mudou alguma coisa, para quem tem de actualizar
 *  a textura saber se vale a pena. */
export function aplicarContornos(modelId: string, resto: ReadonlySet<number>): boolean {
  const antes = emFantasma.get(modelId) ?? new Set<number>()
  const { mostrar, esconder } = diferenca(antes, resto)
  if (resto.size === 0) emFantasma.delete(modelId)
  else emFantasma.set(modelId, new Set(resto))
  return mostrar.length > 0 || esconder.length > 0
}

/** Tira o fantasma deste modelo. Devolve se havia alguma coisa. */
export function largarContornos(modelId: string): boolean {
  const havia = (emFantasma.get(modelId)?.size ?? 0) > 0
  emFantasma.delete(modelId)
  return havia
}

/** O modelo saiu da cena. */
export function esquecerModelo(modelId: string): void {
  emFantasma.delete(modelId)
}
